/**
 * AppleSignInButton — scaffolding for "Continue with Apple".
 *
 * Renders nothing until the backend reports apple_signin enabled.
 * When enabled, loads Apple's official JS SDK on demand, runs the
 * `AppleID.auth.signIn({ usePopup: true })` web flow, POSTs the
 * returned id_token + nonce to /api/auth/apple/callback, hydrates
 * the AuthContext, and routes to /dashboard.
 *
 * Same embedded-WebView guard as the Google button.
 */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Apple } from 'lucide-react';
import { detectEmbeddedBrowser } from '@/utils/embeddedBrowser';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APPLE_SDK = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const EMBEDDED = detectEmbeddedBrowser();

// Module-level cache: load the SDK exactly once across all button mounts.
let _sdkPromise = null;
const loadAppleSdk = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.AppleID) return Promise.resolve();
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SDK}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = APPLE_SDK;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return _sdkPromise;
};

const AppleSignInButton = ({ testIdPrefix = '' }) => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  // Check backend feature flag. Hidden if flag off — single round-trip.
  useEffect(() => {
    mounted.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/apple/status`);
        if (mounted.current) setEnabled(!!data?.enabled);
      } catch (_) {
        // Status endpoint never throws in normal operation — if it does,
        // we silently keep the button hidden rather than show a broken UI.
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  if (!enabled) return null;  // dormant until operator enables in env

  const handleClick = async () => {
    if (busy) return;
    if (EMBEDDED.isEmbedded) {
      toast.error("Sign in with Apple doesn't work inside in-app browsers. Please open this page in your browser.");
      return;
    }
    setBusy(true);
    try {
      await loadAppleSdk();
      if (!window.AppleID?.auth) throw new Error('Apple SDK failed to initialise');

      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
      // URLS, THIS BREAKS THE AUTH. Apple's Services ID has the allowed
      // origins registered in its dashboard — we use window.location.origin
      // at runtime.
      const nonce = (window.crypto?.randomUUID?.() ||
        Math.random().toString(36).slice(2) + Date.now().toString(36));

      // Backend status endpoint returns the Services ID so we don't
      // hardcode a build-time client_id here. (Future enhancement — for
      // now we ask the backend at click time.)
      const cfg = (await axios.get(`${API}/auth/apple/status`)).data || {};
      const servicesId = cfg.services_id || cfg.client_id;
      if (!servicesId) {
        // Backwards-safe: fall back to using window.location.host-derived
        // services_id (operator-set in env). If absent, the backend will
        // reject the id_token at callback time with a clear error.
        // No-op here.
      }

      window.AppleID.auth.init({
        clientId: servicesId,
        scope: 'name email',
        redirectURI: window.location.origin + '/dashboard',
        usePopup: true,
      });

      const resp = await window.AppleID.auth.signIn({ nonce, usePopup: true });
      const idToken = resp?.authorization?.id_token;
      if (!idToken) throw new Error('Apple did not return an id_token');

      let browserTz = null;
      try { browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) {}

      const headers = {};
      if (browserTz) headers['X-Browser-Timezone'] = browserTz;

      const { data } = await axios.post(
        `${API}/auth/apple/callback`,
        { id_token: idToken, nonce },
        { withCredentials: true, headers },
      );

      if (data?.tokens && typeof window !== 'undefined') {
        window.__hlAuth?.storeTokens?.(data.tokens);
      }
      try { localStorage.setItem('healthloop_user', JSON.stringify(data)); } catch (_) {}
      await refreshUser();
      toast.success(`Welcome${data?.name ? ', ' + data.name : ''}!`);
      navigate(data?.role === 'admin' ? '/admin' : '/dashboard', { replace: true });
    } catch (err) {
      const code = err?.error || err?.response?.data?.detail || err?.message;
      if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize') {
        // Silent — user just dismissed the popup
      } else {
        toast.error(typeof code === 'string' ? code : 'Apple sign-in failed. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={busy || EMBEDDED.isEmbedded}
        onClick={handleClick}
        className={`w-full h-12 mb-4 flex items-center justify-center gap-3 rounded-xl border bg-black text-white font-medium transition-all duration-200 ${
          (busy || EMBEDDED.isEmbedded)
            ? 'border-slate-700 opacity-50 cursor-not-allowed'
            : 'border-black hover:bg-slate-900 active:scale-[0.97] shadow-sm hover:shadow'
        }`}
        data-testid={`${testIdPrefix}continue-with-apple-button`}
        aria-label="Continue with Apple"
        aria-disabled={busy || EMBEDDED.isEmbedded}
      >
        <Apple className="h-5 w-5" aria-hidden="true" />
        <span>{busy ? 'Signing in…' : 'Continue with Apple'}</span>
      </button>
    </>
  );
};

export default AppleSignInButton;
